import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { seriesList } from "@/mocks/series";
import type { Series } from "@/types";

const featured: Series[] = seriesList
  .filter((item) => item.score >= 8.8)
  .slice(0, 5);

export default function FeaturedCarousel() {
  const [active, setActive] = useState(0);

  const go = (next: number) => {
    setActive((next + featured.length) % featured.length);
  };

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % featured.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, []);

  const current = featured[active];

  return (
    <section className="relative">
      <div className="relative h-[380px] w-full overflow-hidden rounded-lg border border-background-200 bg-primary-950 md:h-[480px]">
        {featured.map((item, index) => (
          <img
            key={item.id}
            src={item.banner}
            alt={`${item.title} 剧照`}
            title={`${item.title} · ai短剧焦点推荐`}
            className={`absolute inset-0 h-full w-full object-cover object-center transition-opacity duration-700 ${
              index === active ? "opacity-100" : "opacity-0"
            }`}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/70 to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/80 to-transparent"></div>

        <div className="absolute inset-x-0 bottom-0 p-6 md:p-10">
          <div key={current.id} className="max-w-2xl animate-fade-up">
            <div className="flex flex-wrap items-center gap-3 text-xs">
              <span className="rounded-md bg-accent-500 px-2.5 py-1 font-semibold text-foreground-950">
                焦点推荐
              </span>
              <span className="rounded-md bg-background-50/15 px-2.5 py-1 font-medium text-background-50">
                {current.categoryName}
              </span>
              <span className="flex items-center gap-1 font-medium text-secondary-100">
                <i className="ri-star-fill text-accent-400"></i>
                {current.score} 分
              </span>
              <span className="font-medium text-secondary-100">{current.year}</span>
            </div>

            <h2 className="mt-4 font-heading text-2xl font-bold text-background-50 md:text-4xl">
              {current.title}
            </h2>
            <p className="mt-3 line-clamp-2 max-w-xl text-sm leading-relaxed text-secondary-100">
              {current.description}
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${current.id}`}
                className="inline-flex items-center gap-2 rounded-md bg-background-50 px-5 py-2.5 text-sm font-semibold text-primary-800 transition-colors hover:bg-accent-400 hover:text-foreground-950"
              >
                <i className="ri-play-circle-line text-lg"></i>
                <span className="whitespace-nowrap">查看详情</span>
              </Link>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => go(active - 1)}
                  aria-label="上一部推荐"
                  className="flex h-10 w-10 items-center justify-center rounded-md border border-background-50/30 text-background-50 transition-colors hover:bg-background-50/15"
                >
                  <i className="ri-arrow-left-s-line text-xl"></i>
                </button>
                <button
                  type="button"
                  onClick={() => go(active + 1)}
                  aria-label="下一部推荐"
                  className="flex h-10 w-10 items-center justify-center rounded-md border border-background-50/30 text-background-50 transition-colors hover:bg-background-50/15"
                >
                  <i className="ri-arrow-right-s-line text-xl"></i>
                </button>
              </div>
            </div>
          </div>

          <div className="mt-6 flex items-center gap-2">
            {featured.map((item, index) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setActive(index)}
                aria-label={`查看推荐：${item.title}`}
                className={`h-1.5 rounded-full transition-all ${
                  index === active ? "w-8 bg-accent-500" : "w-4 bg-background-50/40 hover:bg-background-50/70"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}